import {
  Box,
  FormHelperText,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { BOTTOM_MENU_HEIGHT } from "Components/Layout/Menu/BottomMenu";
import { Account } from "Components/Shared/Account/Account";
import {
  type BankAccountListItem,
  BankAccountStatus,
} from "Infrastructure/Api/Api";
import { useRef, useState } from "react";
import { Controller, type Control } from "react-hook-form";
import type { PaymentDrawerFormData } from "Components/Chat/PaymentDrawerForm";
import { Resources, useResource } from "Translations/Resources";

const SPACING_FROM_MENU = 12;
const MIN_MENU_HEIGHT = 150;

type Props = {
  control: Control<PaymentDrawerFormData>;
  label: string;
  accounts: BankAccountListItem[];
};

export const BankAccountIDSelect = ({ control, label, accounts }: Props) => {
  const [pxUntilBottomOfScreen, setPxUntilBottomOfScreen] = useState("");
  const [pxUntilTopOfScreen, setPxUntilTopOfScreen] = useState("");
  const [shouldOpenToTop, setShouldOpenToTop] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);
  const { t } = useResource();

  const determineMenuPosition = () => {
    if (selectRef.current) {
      const rect = selectRef.current.getBoundingClientRect();
      const distanceToBottom = window.innerHeight - rect.bottom;
      const distanceToTop = rect.top;
      setPxUntilBottomOfScreen(`${distanceToBottom}px`);
      setPxUntilTopOfScreen(`${distanceToTop}px`);
      setShouldOpenToTop(distanceToBottom < MIN_MENU_HEIGHT);
    }
  };

  const maxHeight = shouldOpenToTop
    ? `calc(${pxUntilTopOfScreen} - ${SPACING_FROM_MENU}px)`
    : `calc(${pxUntilBottomOfScreen} - ${BOTTOM_MENU_HEIGHT} - ${SPACING_FROM_MENU}px)`;

  const anchorOrigin = shouldOpenToTop
    ? { vertical: "top" as const, horizontal: "left" as const }
    : { vertical: "bottom" as const, horizontal: "left" as const };

  const transformOrigin = shouldOpenToTop
    ? { vertical: "bottom" as const, horizontal: "left" as const }
    : { vertical: "top" as const, horizontal: "left" as const };

  return (
    <Controller
      control={control}
      name="bankAccountID"
      render={({ field, fieldState }) => (
        <div>
          <Typography fontWeight={400} mb={1}>
            {label}
          </Typography>
          <Select
            ref={selectRef}
            value={field.value ?? ""}
            onChange={(e) => field.onChange(Number(e.target.value))}
            error={!!fieldState.error}
            onOpen={determineMenuPosition}
            sx={({ palette }) => ({
              width: "100%",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: fieldState.error
                  ? palette.error.main
                  : palette.divider,
                borderRadius: 1,
              },
            })}
            MenuProps={{
              slotProps: {
                paper: {
                  sx: {
                    maxHeight,
                    overflowY: "auto",
                  },
                },
              },
              anchorOrigin,
              transformOrigin,
            }}
            displayEmpty
            renderValue={(value) => {
              if (!value) {
                return (
                  <Box minHeight="1.4375em" display="flex" alignItems="center">
                    <Typography>
                      {t(Resources.Pages.Content.BankAccounts.NoAccountSet)}
                    </Typography>
                  </Box>
                );
              }
              const selected = accounts.find((a) => a.bankAccountID === value);
              if (!selected) {
                return null;
              }
              return (
                <Box width="100%" display="flex" justifyContent="space-between">
                  <Account
                    {...selected}
                    balanceVariant="visible"
                    areChipsHidden
                    isNameTruncated
                  />
                </Box>
              );
            }}
          >
            {accounts.map((account) => (
              <MenuItem
                key={account.bankAccountID}
                value={account.bankAccountID}
                disabled={
                  account.status !== BankAccountStatus.Active &&
                  account.status !== BankAccountStatus.Restricted
                }
              >
                <Box width="100%" display="flex" justifyContent="space-between">
                  <Account
                    {...account}
                    balanceVariant="visible"
                    areChipsHidden
                    isNameTruncated
                  />
                </Box>
              </MenuItem>
            ))}
          </Select>
          {fieldState.error?.message && (
            <FormHelperText error sx={{ mt: 0.5 }}>
              {fieldState.error.message}
            </FormHelperText>
          )}
        </div>
      )}
    />
  );
};
